import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PencilSimple, CaretRight, Tag, Clock, LinkSimple, ListBullets, Lock, Printer, FileText } from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import { knowledgeApi } from './knowledgeApi';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import TraceabilityPanel from '@/pages/trace/TraceabilityPanel';
import { formatDateTime } from '@/utils/datetime';

export default function ArticleView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useOutletContext();
  const [article, setArticle] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);
  const [toc, setToc] = useState([]);
  // A4 document preview — local UI state only, never touches article data.
  const [a4, setA4] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setErr('');
    knowledgeApi.article(id)
      .then((a) => {
        if (!active) return;
        setArticle(a);
        // URL が UUID または旧 slug の場合、正式な slug へ静かに置換
        if (a.slug && id !== a.slug) {
          navigate(`/knowledge/p/${a.slug}`, { replace: true });
        }
      })
      .catch((e) => { if (active) setErr(e?.response?.data?.detail || 'ページを表示できません'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, navigate]);

  // 目次（本文の h1/h2/h3 から自動生成）
  useEffect(() => {
    if (!article) { setToc([]); return; }
    const root = contentRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll('h1, h2, h3')).map((el, i) => {
      const hid = `kb-h-${i}`;
      el.id = hid;
      return { id: hid, text: (el.textContent || '').trim(), level: Number(el.tagName[1]) };
    }).filter((h) => h.text);
    setToc(items);
  }, [article, a4]);

  // 記事内部のアプリ内リンクは SPA ナビゲーションに変換して白画面を防ぐ
  useEffect(() => {
    const root = contentRef.current;
    if (!article || !root) return;
    const handler = (e) => {
      const anchor = e.target?.closest?.('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      // 外部リンク・ハッシュ・別タブは通常挙動
      if (anchor.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      if (!href.startsWith('/')) return;
      // アプリ内ルートのみ捕捉
      const internalPrefixes = ['/planner/', '/knowledge/', '/engineering/', '/it/', '/mfg/', '/marketing/', '/bihin/'];
      if (!internalPrefixes.some((p) => href.startsWith(p))) return;
      e.preventDefault();
      navigate(href);
    };
    root.addEventListener('click', handler);
    return () => root.removeEventListener('click', handler);
  }, [article, navigate]);

  const scrollToHeading = (hid) => { const el = document.getElementById(hid); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const copyLink = async () => {
    const url = `${window.location.origin}/knowledge/p/${article?.slug || id}`;
    try { await navigator.clipboard.writeText(url); toast.success('リンクをコピーしました'); }
    catch { toast.error('コピーできませんでした'); }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-400" data-testid="kb-view-loading">読み込み中…</div>;
  if (err) return <div className="p-8 text-sm text-rose-500" data-testid="kb-view-error">{err}</div>;
  if (!article) return null;

  if (article.locked) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-8" data-testid="kb-article-locked">
        {article.breadcrumb?.length > 0 && (
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-zinc-400">
            {article.breadcrumb.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1">
                <Link to={`/knowledge/p/${c.slug || c.id}`} className="hover:text-blue-600">{c.icon} {c.title}</Link>
                <CaretRight size={11} />
              </span>
            ))}
          </nav>
        )}
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-zinc-700">
          {article.icon && <span>{article.icon}</span>}
          <span data-testid="kb-article-title">{article.title}</span>
        </h1>
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
          <Lock size={40} weight="fill" className="mx-auto mb-3 text-amber-500" />
          <p className="text-sm font-medium text-amber-800">このページは公開範囲が限定されています</p>
          <p className="mt-1 text-xs text-amber-700">閲覧する権限がありません。必要な場合は管理者にお問い合わせください。</p>
        </div>
      </div>
    );
  }

  const updated = article.updated_at ? formatDateTime(article.updated_at) : '';

  return (
    <div className={a4 ? 'knowledge-a4-preview pt-6' : 'mx-auto max-w-4xl px-6 py-8'} data-testid="kb-article-view">
      <div className={a4 ? 'mx-auto w-[min(210mm,100%)] px-1' : ''}>
        {article.breadcrumb?.length > 0 && (
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-zinc-400" data-testid="kb-breadcrumb">
            {article.breadcrumb.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1">
                <Link to={`/knowledge/p/${c.slug || c.id}`} className="hover:text-blue-600">{c.icon} {c.title}</Link>
                <CaretRight size={11} />
              </span>
            ))}
          </nav>
        )}

        {/* document toolbar — screen only, never printed */}
        <div className="kb-no-print knowledge-document-toolbar mb-3 flex flex-wrap items-center justify-end gap-2" data-testid="kb-doc-toolbar">
          <button type="button" data-testid="kb-copy-link-btn" onClick={copyLink} title="リンクをコピー"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50">
            <LinkSimple size={15} />リンク
          </button>
          <button type="button" data-testid="kb-a4-toggle" onClick={() => setA4((v) => !v)} title="A4プレビュー"
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm ${a4 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50'}`}>
            <FileText size={15} weight={a4 ? 'fill' : 'regular'} />A4プレビュー
          </button>
          <button type="button" data-testid="kb-print-btn" onClick={() => window.print()} title="印刷 / PDF出力"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50">
            <Printer size={15} />印刷 / PDF出力
          </button>
          {canEdit && (
            <button type="button" data-testid="kb-edit-btn" onClick={() => navigate(`/knowledge/p/${article.slug || id}/edit`)}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50">
              <PencilSimple size={15} />編集
            </button>
          )}
        </div>
      </div>

      <div className={a4 ? 'knowledge-a4-page' : ''}>
        <div className="knowledge-print-document knowledge-document-content">
          <h1 className="flex items-start gap-2 text-3xl font-bold tracking-tight text-zinc-900">
            {article.icon && <span>{article.icon}</span>}
            <span data-testid="kb-article-title">{article.title}</span>
          </h1>

          {!a4 && (
            <div className="kb-no-print mt-2 mb-6 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
              <span className={`rounded-full px-2 py-0.5 font-medium ${article.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="kb-status-badge">
                {article.status === 'published' ? '公開' : '下書き'}
              </span>
              {article.category && <span className="rounded bg-zinc-100 px-2 py-0.5 text-zinc-500">{article.category}</span>}
              {(article.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-zinc-400"><Tag size={11} />{t}</span>
              ))}
              {updated && <span className="inline-flex items-center gap-1"><Clock size={11} />{updated}・{article.updated_by_name || ''}</span>}
            </div>
          )}

          {!a4 && toc.length >= 2 && (
            <nav className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="kb-toc">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400"><ListBullets size={13} />目次</div>
              <ul className="space-y-0.5">
                {toc.map((h) => (
                  <li key={h.id} style={{ paddingLeft: `${(h.level - 1) * 12}px` }}>
                    <button type="button" onClick={() => scrollToHeading(h.id)} data-testid={`kb-toc-${h.id}`}
                      className="text-left text-sm text-zinc-600 hover:text-blue-600">{h.text}</button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <article ref={contentRef} className={`tiptap kb-readonly ${a4 ? 'mt-6' : ''}`} data-testid="kb-article-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content_html || '<p class="text-zinc-400">（内容がありません）</p>', { ADD_ATTR: ['target', 'data-kb-article-id'] }) }} />

          <LinkedDocsPanel targetType="kb_page" targetId={article.id} className="kb-no-print mt-8 pt-4 border-t border-zinc-200" />
          <TraceabilityPanel entityType="kb_page" entityId={article.id} className="kb-no-print mt-6 pt-4 border-t border-zinc-200" />
        </div>
      </div>
    </div>
  );
}
